import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { listSupportDriverRides, getSupportDriverRideDetail } from "../../../../../api/adminSupportApi";

/* --------------------- helpers --------------------- */
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function up(x) {
  return safeStr(x).toUpperCase();
}
function oneLine(s, fallback = "—") {
  const x = safeStr(s);
  return x ? x : fallback;
}
function numStr(v, fallback = "—") {
  if (v == null) return fallback;
  const s = String(v);
  return s.trim() ? s : fallback;
}

/**
 * ✅ Consistent format everywhere:
 * "05 Feb 2026, 8:15 AM"
 */
const DT_FMT = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function toDateSafe(v) {
  if (!v) return null;
  try {
    const d = new Date(String(v));
    if (!Number.isNaN(d.getTime())) return d;
  } catch (_) {}
  return null;
}

function fmtDT(v) {
  const d = toDateSafe(v);
  if (!d) return v ? String(v) : "—";
  return DT_FMT.format(d);
}

/**
 * Handles date+time parts consistently.
 * - datePart: "2026-02-05" (preferred)
 * - timePart: "08:15" / "08:15:30"
 */
function fmtDTFromParts(datePart, timePart) {
  const dp = safeStr(datePart);
  const tp = safeStr(timePart);
  if (!dp && !tp) return "—";

  if (dp && tp && /^\d{4}-\d{2}-\d{2}$/.test(dp)) {
    const iso = `${dp}T${tp}`;
    const d = toDateSafe(iso);
    if (d) return DT_FMT.format(d);
  }
  if (dp && /^\d{4}-\d{2}-\d{2}$/.test(dp)) {
    const d = toDateSafe(`${dp}T00:00:00`);
    if (d) return DT_FMT.format(d);
  }
  if (dp && tp) return `${dp} ${tp}`;
  return dp || tp || "—";
}

function Chip({ children, tone = "neutral" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <span className={classNames("inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-semibold", cls)}>
      {children}
    </span>
  );
}

function toneRideStatus(s) {
  const v = up(s);
  if (v === "ENDED" || v === "COMPLETED") return "good";
  if (v === "CANCELLED" || v === "FAILED") return "bad";
  if (v === "STARTED" || v === "ARRIVED" || v === "ARRIVED_AT_PICKUP" || v === "IN_PROGRESS") return "warn";
  return "neutral";
}

/**
 * ✅ Status update source (best effort)
 * We try multiple fields your backend may provide.
 */
function statusSourceForRide(r) {
  if (!r) return "SYSTEM";

  // treat any reconcile signal as RECONCILED
  if (r.reconciledAt) return "RECONCILED";
  if (r.reconciledMode) return "RECONCILED";
  if (r.reconciledByAdminId != null) return "RECONCILED";
  if (r.reconciledBy != null) return "RECONCILED";

  // everything else => SYSTEM
  return "SYSTEM";
}


function Drawer({ open, onClose, title, children, widthClass = "max-w-5xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={classNames("absolute right-0 top-0 h-full w-full bg-white shadow-xl", widthClass)}>
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="truncate text-[14px] font-extrabold text-slate-900">{title}</div>
            <div className="mt-0.5 text-[12px] text-slate-500">Full ride context for driver support</div>
          </div>
          <button
            className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="h-[calc(100%-73px)] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-[13px] font-semibold text-slate-900 truncate">{oneLine(value)}</div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
        {right}
      </div>
      <div className="px-4 py-4">{children}</div>
    </div>
  );
}

function PaginationFooter({ page, totalPages, size, onSize, onPrev, onNext, totalElements }) {
  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          Page <span className="font-semibold text-slate-700">{page + 1}</span> of{" "}
          <span className="font-semibold text-slate-700">{Math.max(1, totalPages)}</span>
          {Number.isFinite(Number(totalElements)) ? (
            <>
              {" "}
              <span className="text-slate-300">•</span>{" "}
              <span className="font-semibold text-slate-700">{Number(totalElements).toLocaleString("en-IN")}</span> total
            </>
          ) : null}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold text-slate-500">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => onSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50 outline-none"
              >
                {[10, 20, 30, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">▼</span>
            </div>
          </div>

          <div className="text-sm text-slate-700">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={page <= 0}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={page + 1 >= totalPages}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SupportDriverRidesTab() {
  const ctx = useOutletContext() || {};
  const driverId = ctx.driverId;

  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [openRideId, setOpenRideId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const rows = data?.content || [];
  const totalPages = Number(data?.totalPages ?? 1);
  const totalElements = Number(data?.totalElements ?? 0);

  const queryKey = useMemo(() => `${driverId}|${status}|${q}|${page}|${size}`, [driverId, status, q, page, size]);

  useEffect(() => {
    if (!driverId) return;
    let alive = true;

    setLoading(true);
    setErr("");

    listSupportDriverRides(driverId, {
      status: status && status !== "ALL" ? status : undefined,
      q: safeStr(q) || undefined,
      page,
      size,
    })
      .then((res) => {
        if (!alive) return;
        setData(res);
      })
      .catch((e) => alive && setErr(e?.message || "Failed to load rides"))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [queryKey, driverId]);

  useEffect(() => {
    if (!openRideId || !driverId) return;
    let alive = true;

    setDetail(null);
    setDetailLoading(true);

    getSupportDriverRideDetail(driverId, openRideId)
      .then((res) => alive && setDetail(res))
      .catch(() => alive && setDetail(null))
      .finally(() => alive && setDetailLoading(false));

    return () => {
      alive = false;
    };
  }, [openRideId, driverId]);

  // allow ESC close drawer (same as bookings tab)
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpenRideId(null);
    }
    if (!!openRideId) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openRideId]);

  const ride = detail?.ride || null;
  const booking = detail?.booking || null;

  // Clean table cell values
  function scheduleCell(r) {
    if (r.pickupDate && r.pickupTime) return fmtDTFromParts(r.pickupDate, r.pickupTime);
    if (r.pickupAt) return fmtDT(r.pickupAt);
    return "—";
  }

  function dropCell(r) {
    if (r.dropDate && r.dropTime) return fmtDTFromParts(r.dropDate, r.dropTime);
    if (r.dropAt) return fmtDT(r.dropAt);
    return "—";
  }

 function statusSourceCell(r) {
  const src = statusSourceForRide(r);
  return src === "RECONCILED" ? <Chip tone="warn">RECONCILED</Chip> : <Chip tone="neutral">SYSTEM</Chip>;
}


  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="border-b border-slate-200 px-5 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Rides</div>
          <div className="mt-0.5 text-[12px] text-slate-500">
            {loading ? "Loading…" : `${totalElements.toLocaleString("en-IN")} total`}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Search rideId / bookingId / regNo / user phone"
            className="h-10 w-full sm:w-[360px] rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
          />

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
          >
            {["ALL", "CREATED", "ARRIVED", "STARTED", "ENDED", "CANCELLED", "FAILED", "IN_PROGRESS", "ARRIVED_AT_PICKUP"].map(
              (s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              )
            )}
          </select>
        </div>
      </div>

      {err ? <div className="border-b border-slate-200 px-5 py-3 text-[12px] font-semibold text-rose-600">{err}</div> : null}

      <div className="w-full overflow-x-auto">
        <table className="min-w-[1200px] w-full table-fixed">
          <colgroup>
            <col style={{ width: "110px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "220px" }} />
            <col style={{ width: "220px" }} />
            <col style={{ width: "180px" }} />
            <col style={{ width: "160px" }} />
          </colgroup>

          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Ride ID</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Ride Status</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Booking</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Vehicle</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Pickup</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Drop</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Status Source</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Updated</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-sm font-semibold text-slate-600">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-14 text-center">
                  <div className="text-sm font-semibold text-slate-900">No rides</div>
                  <div className="mt-1 text-[12px] text-slate-500">Try changing filters or search.</div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.rideId}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenRideId(r.rideId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setOpenRideId(r.rideId);
                  }}
                  className="cursor-pointer hover:bg-slate-50 focus:outline-none"
                  title="Click to open details"
                >
                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 tabular-nums">{numStr(r.rideId)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <Chip tone={toneRideStatus(r.status)}>{up(r.status) || "—"}</Chip>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 tabular-nums">{numStr(r.bookingId)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 tabular-nums">{oneLine(r.vehicleRegNo)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{oneLine(r.pickupLocation)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">
                      {oneLine(r.destination || (Array.isArray(r.destinations) && r.destinations[0]) || "—")}
                    </div>
                  </td>

                  <td className="px-4 py-4">{statusSourceCell(r)}</td>

                  <td className="px-4 py-4">
                    <div className="text-[12px] font-semibold text-slate-900">{fmtDT(r.updatedAt)}</div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationFooter
        page={page}
        totalPages={totalPages}
        size={size}
        onSize={(n) => {
          setSize(n);
          setPage(0);
        }}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        totalElements={totalElements}
      />

      <Drawer
        open={!!openRideId}
        onClose={() => setOpenRideId(null)}
        widthClass="max-w-5xl"
        title={openRideId ? `Ride #${openRideId}` : "Ride"}
      >
        {detailLoading ? (
          <div className="p-6 text-sm font-semibold text-slate-600">Loading…</div>
        ) : !detail ? (
          <div className="p-6 text-sm font-semibold text-slate-600">No detail</div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Summary strip (same feel as bookings drawer) */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip tone={toneRideStatus(ride?.status)}>{up(ride?.status) || "—"}</Chip>
                  {booking?.status ? <Chip tone="neutral">{up(booking?.status)}</Chip> : null}
                  {ride?.reconciledAt ? <Chip tone="warn">RECONCILED</Chip> : null}
                </div>
                <div className="text-[12px] text-slate-600">
                  Created: <span className="font-semibold">{fmtDT(ride?.createdAt)}</span>{" "}
                  <span className="text-slate-300">•</span> Updated:{" "}
                  <span className="font-semibold">{fmtDT(ride?.updatedAt)}</span>
                </div>
              </div>
            </div>

            {/* Top overview cards */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <Section title="Ride overview">
                <div className="grid grid-cols-1 gap-3">
                  <KV label="Ride ID" value={ride?.rideId} />
                  <KV label="Ride status" value={up(ride?.status) || "—"} />
                  <KV label="Status source" value={statusSourceForRide(ride)} />
                </div>
              </Section>

              <Section title="Booking overview">
                <div className="grid grid-cols-1 gap-3">
                  <KV label="Booking ID" value={ride?.bookingId || booking?.bookingId} />
                  <KV label="Booking status" value={booking?.status || ride?.bookingStatus} />
                  <KV label="Payment status" value={booking?.paymentStatus || booking?.payment_state || "—"} />
                </div>
              </Section>

              <Section title="Vehicle overview">
                <div className="grid grid-cols-1 gap-3">
                  <KV label="Vehicle reg no" value={ride?.vehicleRegNo} />
                  <KV label="Owner name" value={ride?.ownerName || booking?.ownerName} />
                  <KV label="Owner phone" value={ride?.ownerPhone || booking?.ownerPhone} />
                </div>
              </Section>
            </div>

            {/* Schedule & route */}
            <Section title="Route & schedule">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <KV label="Pickup location" value={ride?.pickupLocation} />
                <KV label="Drop location" value={ride?.destination || (ride?.destinations?.[0] ?? "—")} />
                <KV
                  label="Pickup time"
                  value={
                    ride?.pickupDate && ride?.pickupTime
                      ? fmtDTFromParts(ride.pickupDate, ride.pickupTime)
                      : fmtDT(ride?.pickupAt)
                  }
                />
                <KV
                  label="Drop time"
                  value={
                    ride?.dropDate && ride?.dropTime
                      ? fmtDTFromParts(ride.dropDate, ride.dropTime)
                      : fmtDT(ride?.dropAt)
                  }
                />
              </div>
            </Section>

            {/* Timeline & reconcile */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <Section title="Timeline">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <KV label="Arrived at" value={fmtDT(ride?.arrivedAt)} />
                  <KV label="Started at" value={fmtDT(ride?.startTime)} />
                  <KV label="Ended at" value={fmtDT(ride?.endTime)} />
                  <KV label="Last updated" value={fmtDT(ride?.updatedAt)} />
                </div>
              </Section>

              <Section title="Reconcile">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <KV label="Reconciled at" value={fmtDT(ride?.reconciledAt)} />
                  <KV label="Reconciled mode" value={ride?.reconciledMode || "—"} />
                  <KV
                    label="Reconciled by admin"
                    value={ride?.reconciledByAdminId != null ? `#${ride.reconciledByAdminId}` : "—"}
                  />
                  <KV label="Status source" value={statusSourceForRide(ride)} />
                </div>
              </Section>
            </div>

            {/* Request events (if present) */}
            <Section title="Request events">
              {Array.isArray(detail.requestEvents) && detail.requestEvents.length ? (
                <div className="space-y-2">
                  {detail.requestEvents.slice(0, 50).map((ev) => (
                    <div key={ev.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2 text-[12px]">
                        <span className="font-extrabold text-slate-900">{ev.eventType || "EVENT"}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-slate-600">{fmtDT(ev.createdAt)}</span>
                        {ev.traceId ? (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className="text-slate-500 tabular-nums">{ev.traceId}</span>
                          </>
                        ) : null}
                      </div>
                      {ev.message ? <div className="mt-1 text-[12px] text-slate-700">{ev.message}</div> : null}
                      <div className="mt-1 text-[11px] text-slate-500 tabular-nums">
                        {oneLine(ev.actorType)} {ev.actorId != null ? `#${ev.actorId}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm font-semibold text-slate-600">No events</div>
              )}
            </Section>
          </div>
        )}
      </Drawer>
    </div>
  );
}
